<script setup lang="ts">
/**
 * Быстрый переход по Cmd+K: поиск по базе знаний и прыжок на задачу по ключу.
 *
 * Своего экрана поиска у базы знаний нет — дерево показывает структуру, а не
 * содержимое, и найти страницу в чужом пространстве иначе нечем.
 *
 * Ключ задачи распознаём здесь же: «DEV-1421» набирают чаще любого запроса, а
 * искать его полнотекстом по страницам бессмысленно. Полнотекстовый поиск по
 * задачам живёт в списке проекта и сюда не заведён (docs/design-gaps.md).
 */
import { Book, CornerDownLeft } from 'lucide-vue-next'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { PALETTE_EVENT } from '@/components/palette'
import { searchPages, type PageHit } from '@/features/knowledge/api'

/** Пауза перед запросом: запрос на каждую букву кладёт FTS зря. */
const SEARCH_DELAY = 250
const ISSUE_KEY = /^[A-Za-z]+-\d+$/

const { t } = useI18n()
const router = useRouter()

const isOpen = ref(false)
const query = ref('')
const hits = ref<PageHit[]>([])
const active = ref(0)
const field = ref<HTMLInputElement | null>(null)

let timer: ReturnType<typeof setTimeout> | undefined

const issueKey = computed(() => {
  const needle = query.value.trim()

  return ISSUE_KEY.test(needle) ? needle.toUpperCase() : ''
})

/** Задача ключом стоит первой строкой, страницы — под ней. */
const total = computed(() => (issueKey.value ? 1 : 0) + hits.value.length)

const open = async () => {
  isOpen.value = true

  await nextTick()

  field.value?.focus()
}

const close = () => {
  isOpen.value = false
  query.value = ''
  hits.value = []
  active.value = 0
}

const run = async () => {
  const needle = query.value.trim()

  if (needle.length < 2 || issueKey.value) {
    hits.value = []

    return
  }

  try {
    hits.value = await searchPages(needle)
  } catch {
    // Поиск — вспомогательный путь: если сервис молчит, палитра просто пуста.
    hits.value = []
  }
}

const go = async (index: number) => {
  const target = index - (issueKey.value ? 1 : 0)

  if (issueKey.value && index === 0) {
    await router.push(`/issue/${issueKey.value}`)
  } else {
    const hit = hits.value[target]

    if (!hit) return

    await router.push(`/kb/${hit.id}`)
  }

  close()
}

const onKey = (event: KeyboardEvent) => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()

    if (isOpen.value) close()
    else void open()

    return
  }

  if (!isOpen.value) return

  if (event.key === 'Escape') return close()

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    active.value = total.value ? (active.value + 1) % total.value : 0
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    active.value = total.value ? (active.value - 1 + total.value) % total.value : 0
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    void go(active.value)
  }
}

watch(query, () => {
  active.value = 0

  if (timer) clearTimeout(timer)

  timer = setTimeout(() => void run(), SEARCH_DELAY)
})

// На телефоне сочетания нет: палитру открывает вкладка «Поиск».
const onExternal = () => void open()

onMounted(() => {
  window.addEventListener('keydown', onKey)
  window.addEventListener(PALETTE_EVENT, onExternal)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKey)
  window.removeEventListener(PALETTE_EVENT, onExternal)

  if (timer) clearTimeout(timer)
})
</script>

<template>
  <div v-if="isOpen" class="cpal" role="dialog" :aria-label="t('palette.title')" @click.self="close">
    <div class="cpal__box">
      <input
        ref="field"
        v-model="query"
        class="cpal__field"
        :placeholder="t('palette.placeholder')"
        :aria-label="t('palette.title')"
      />

      <ul v-if="total" class="cpal__list">
        <li v-if="issueKey">
          <button
            type="button"
            class="cpal__row"
            :class="{ 'cpal__row--active': active === 0 }"
            @click="go(0)"
          >
            <CornerDownLeft class="cpal__icon" :size="14" />
            <span class="cpal__title">{{ t('palette.openIssue', { key: issueKey }) }}</span>
          </button>
        </li>

        <li v-for="(hit, index) in hits" :key="hit.id">
          <button
            type="button"
            class="cpal__row"
            :class="{ 'cpal__row--active': active === index + (issueKey ? 1 : 0) }"
            @click="go(index + (issueKey ? 1 : 0))"
          >
            <Book class="cpal__icon" :size="14" />
            <span class="cpal__text">
              <span class="cpal__title">{{ hit.title }}</span>
              <span v-if="hit.snippet" class="cpal__snippet">{{ hit.snippet }}</span>
            </span>
            <span v-if="hit.spaceKey" class="cpal__space">{{ hit.spaceKey }}</span>
          </button>
        </li>
      </ul>

      <p v-else class="cpal__hint">
        {{ query.trim().length >= 2 ? t('palette.nothing') : t('palette.hint') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.cpal {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  justify-content: center;
  padding-top: 12vh;
  background: rgb(24 24 27 / 32%);
}

.cpal__box {
  display: flex;
  flex-direction: column;
  width: 560px;
  max-width: calc(100vw - 32px);
  max-height: 60vh;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.cpal__field {
  height: 48px;
  padding: 0 16px;
  border: none;
  border-bottom: 1px solid var(--color-border);
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body);
}

.cpal__field:focus {
  outline: none;
}

.cpal__list {
  margin: 0;
  padding: 4px;
  overflow-y: auto;
  list-style: none;
}

.cpal__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-radius: var(--radius-md);
  background: none;
  font-family: var(--font-family-sans);
  text-align: left;
  cursor: pointer;
}

.cpal__row:hover,
.cpal__row--active {
  background: var(--color-bg-muted);
}

.cpal__icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.cpal__text {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  min-width: 0;
}

.cpal__title {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
}

.cpal__snippet {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.cpal__space {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
}

.cpal__hint {
  margin: 0;
  padding: 16px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}
</style>
